import { Icon } from '@tcweb/connect-ui';
import { Component, For, Show } from 'solid-js';
import {
  ControlMenuItemType,
  ControlMenuNestedSubmenuProps,
  ControlMenuNestedSubmenuSharedProps,
  RadioGroupMenuItem,
  RadioOption,
} from './types';

interface ControlMenuRadioGroupOptionsProps {
  item: RadioGroupMenuItem;
  isMobile: boolean;
  openMenus: Set<string>;
  setOpenMenus(updater: (prev: Set<string>) => Set<string>): void;
  disabled: boolean;
  onNavigate(item: ControlMenuItemType): void;
  onClose?(): void;
  parentOpensUpward?: boolean;
  onRadioOptionClick(e: Event, option: RadioOption, hasChildren: boolean): void;
  submenuComponent: Component<ControlMenuNestedSubmenuProps>;
  submenuProps: ControlMenuNestedSubmenuSharedProps;
}

export const ControlMenuRadioGroupOptions: Component<
  ControlMenuRadioGroupOptionsProps
> = (props) => (
  <div class="flex flex-col w-full">
    <For each={props.item.options}>
      {(option) => {
        const Submenu = props.submenuComponent;
        const isSelected = (): boolean => props.item.value() === option.id;
        const hasChildren = (): boolean => (option.children?.length ?? 0) > 0;

        const handleOptionMouseEnter = (): void => {
          if (!props.isMobile && hasChildren()) {
            props.setOpenMenus((prev) => {
              const next = new Set(prev);
              next.add(option.id);
              return next;
            });
          }
        };

        const handleOptionMouseLeave = (): void => {
          if (!props.isMobile && hasChildren()) {
            props.setOpenMenus((prev) => {
              const next = new Set(prev);
              next.delete(option.id);
              return next;
            });
          }
        };

        return (
          <div
            class="relative"
            onMouseEnter={handleOptionMouseEnter}
            onMouseLeave={handleOptionMouseLeave}
          >
            <div
              class={`flex items-center gap-2 p-2 cursor-pointer ${
                isSelected()
                  ? 'bg-primary-pale'
                  : 'hover:bg-[var(--modus-wc-color-base-100)]'
              } ${props.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              data-testid={'control-menu-dropdown-option-' + option.id}
              aria-selected={isSelected()}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.stopImmediatePropagation();
              }}
              onClick={(e) =>
                props.onRadioOptionClick(e, option, hasChildren())
              }
            >
              <Show when={option.icon}>
                <Show
                  when={typeof option.icon === 'string'}
                  fallback={option.icon}
                >
                  <Icon
                    icon={option.icon as string}
                    class="flex min-w-5 min-h-5 pr-2 text-base-content"
                  />
                </Show>
              </Show>
              <span class="whitespace-nowrap text-base-content flex-1">
                {option.label}
              </span>
              <Show when={option.labelRight}>
                <span class="text-sm opacity-60 whitespace-nowrap">
                  {option.labelRight}
                </span>
              </Show>
              <Show when={hasChildren()}>
                <Icon icon="tc-icon-chevron-right" class="text-base-content" />
              </Show>
            </div>
            <Show
              when={
                !props.isMobile &&
                props.openMenus.has(option.id) &&
                hasChildren()
              }
            >
              <Submenu
                getChildrenItems={() => option.children ?? []}
                {...props.submenuProps}
              />
            </Show>
          </div>
        );
      }}
    </For>
  </div>
);
